import { Option } from '~/components/Select/Select';
import { ShippingCountryType } from '~/constants/automationRuleForm';
import carriers, { carriersInOrder } from '~/constants/carriers';
import countries from '~/constants/countries';
import { currencies, Currency } from '~/constants/currency';
import {
  B13AFilingOption,
  ContentsType,
  EelPfc,
  Incoterm,
  NonDelivery,
  TaxIDTypes,
} from '~/constants/customsDeclaration';
import { DistanceUnit } from '~/constants/distance';
import { OperatorLabel, OperatorString } from '~/constants/operators';
import { storeNames } from '~/constants/stores';
import { WeightUnit } from '~/constants/weight';
import orderBy from 'lodash-es/orderBy';

const noeei3037aOption: Option = {
  label: 'NOEEI 30 37(a)',
  value: EelPfc.NOEEI3037A,
};

const noeei3037fOption: Option = {
  label: 'NOEEI 30 37(f)',
  value: EelPfc.NOEEI3037F,
};

const noeei3037hOption: Option = {
  label: 'NOEEI 30 37(h)',
  value: EelPfc.NOEEI3037H,
};

const noeei3036Option: Option = {
  label: 'NOEEI 30 36',
  value: EelPfc.NOEEI3036,
};

const aesItnOption: Option = {
  label: 'AES / ITN',
  value: EelPfc.AESITN,
};

const eelPfcOptions = [
  noeei3037aOption,
  noeei3037fOption,
  noeei3037hOption,
  noeei3036Option,
  aesItnOption,
];

const documentOption: Option = {
  label: 'documents',
  value: ContentsType.DOCUMENTS,
};
const giftOption: Option = {
  label: 'gift',
  value: ContentsType.GIFT,
};
const sampleOption: Option = {
  label: 'sample',
  value: ContentsType.SAMPLE,
};
const merchandiseOption: Option = {
  label: 'merchandise',
  value: ContentsType.MERCHANDISE,
};
const returnMerchandiseOption: Option = {
  label: 'returnMerchandise',
  value: ContentsType['RETURN_MERCHANDISE'],
};
const humanitarianDonationOption: Option = {
  label: 'humanitarianDonation',
  value: ContentsType['HUMANITARIAN_DONATION'],
};
const otherOption: Option = {
  label: 'other',
  value: ContentsType['OTHER'],
};
const contentsTypeOptions = [
  documentOption,
  giftOption,
  sampleOption,
  merchandiseOption,
  returnMerchandiseOption,
  humanitarianDonationOption,
  otherOption,
];

const dduOption: Option = {
  label: `${Incoterm.DDU} (bill recipient)`,
  value: Incoterm.DDU,
};
const ddpOption: Option = {
  label: `${Incoterm.DDP} (bill sender)`,
  value: Incoterm.DDP,
};
const fcaOption: Option = {
  label: Incoterm.FCA,
  value: Incoterm.FCA,
};
const incotermOptions = [dduOption, ddpOption, fcaOption];

const returnOption: Option = {
  label: 'Return to sender',
  value: NonDelivery.RETURN,
};
const abandonOption: Option = {
  label: 'Abandon',
  value: NonDelivery.ABANDON,
};
const nonDeliveryOptions = [returnOption, abandonOption];

const b13aNotSelectedOption: Option = {
  label: 'None selected',
  value: B13AFilingOption.NONE_SELECTED,
};
const b13aFiledElectronicallyOption: Option = {
  label: 'Filed electronically',
  value: B13AFilingOption.FILED_ELECTRONICALLY,
};
const b13aSummaryReportingOption: Option = {
  label: 'Summary reporting',
  value: B13AFilingOption.SUMMARY_REPORTING,
};
const b13aNotRequiredOption: Option = {
  label: 'Not required',
  value: B13AFilingOption.NOT_REQUIRED,
};
const b13aFilingOptions = [
  b13aNotSelectedOption,
  b13aFiledElectronicallyOption,
  b13aSummaryReportingOption,
  b13aNotRequiredOption,
];

B13AFilingOption;

const hashMapToOptions = (hashMap: Record<string, Option>, x: Option) => {
  hashMap[String(x.value)] = x;
  return hashMap;
};

const taxIdTypeSelectOption: Option = {
  label: 'Select',
  value: '',
};

const taxIdTypeVATOption: Option = {
  label: 'VAT',
  value: TaxIDTypes.VAT,
};

const taxIdTypeIOSSOption: Option = {
  label: 'IOSS',
  value: TaxIDTypes.IOSS,
};

const taxIdTypeEINOption: Option = {
  label: 'EIN',
  value: TaxIDTypes.EIN,
};

const taxIdTypeOptions = [
  taxIdTypeSelectOption,
  taxIdTypeVATOption,
  taxIdTypeIOSSOption,
  taxIdTypeEINOption,
];

const customsDeclaration = {
  eelPfcOptions,
  contentsTypeOptions,
  incotermOptions,
  nonDeliveryOptions,
  b13aFilingOptions,
  eelPfcOptionsMap: eelPfcOptions.reduce(hashMapToOptions, {} as Record<EelPfc | string, Option>),
  contentsTypeOptionsMap: contentsTypeOptions.reduce<Record<ContentsType | string, Option>>(
    hashMapToOptions,
    {},
  ),
  incotermOptionsMap: incotermOptions.reduce<Record<Incoterm | string, Option>>(
    hashMapToOptions,
    {},
  ),
  nonDeliveryOptionsMap: nonDeliveryOptions.reduce<Record<NonDelivery | string, Option>>(
    hashMapToOptions,
    {},
  ),
  b13aFilingOptionsMap: b13aFilingOptions.reduce<Record<B13AFilingOption | string, Option>>(
    hashMapToOptions,
    {},
  ),
  taxIdTypeOptions,
  taxIdTypeOptionsMap: taxIdTypeOptions.reduce<Record<B13AFilingOption | string, Option>>(
    hashMapToOptions,
    {},
  ),
};

const gOption: Option = {
  label: WeightUnit.G,
  value: WeightUnit.G,
};

const kgOption: Option = {
  label: WeightUnit.KG,
  value: WeightUnit.KG,
};

const ozOption: Option = {
  label: WeightUnit.OZ,
  value: WeightUnit.OZ,
};

const lbOption: Option = {
  label: WeightUnit.LB,
  value: WeightUnit.LB,
};

const equalOption: Option = {
  label: OperatorLabel.EQ,
  value: OperatorString.EQ,
};

const includesOption: Option = {
  label: OperatorLabel.INCLUDES,
  value: OperatorString.INCLUDES,
};

const startsWithOption: Option = {
  label: OperatorLabel.STARTS_WITH,
  value: OperatorString.STARTS_WITH,
};

const endsWithOption: Option = {
  label: OperatorLabel.ENDS_WITH,
  value: OperatorString.ENDS_WITH,
};

const lessThanOption: Option = {
  label: OperatorLabel.LT,
  value: OperatorString.LT,
};

const lessThanEqualOption: Option = {
  label: OperatorLabel.LTE,
  value: OperatorString.LTE,
};

const greaterThanOption: Option = {
  label: OperatorLabel.GT,
  value: OperatorString.GT,
};

const greaterThanEqualOption: Option = {
  label: OperatorLabel.GTE,
  value: OperatorString.GTE,
};

const relationalOperatorOptions = [
  equalOption,
  greaterThanOption,
  greaterThanEqualOption,
  lessThanOption,
  lessThanEqualOption,
];
const stringOperatorOptions = [equalOption, includesOption, startsWithOption, endsWithOption];
const weightOptions = [gOption, kgOption, ozOption, lbOption];
const commonWeightOptions: Record<string, Option> = {
  g: gOption,
  kg: kgOption,
  oz: ozOption,
  lb: lbOption,
};

const nonDelayOption = {
  label: 'Send immediately',
  value: 0,
};

// TODO: Impossible to i18n. Need to refactor this out.
const emailNotificationDelayOptions: Option[] = [
  {
    label: '30-minute delay',
    value: 30,
  },
  {
    label: '1-hour delay',
    value: 60,
  },
  {
    label: '3-hour delay',
    value: 180,
  },
  {
    label: '12-hour delay',
    value: 720,
  },
  {
    label: '24-hour delay',
    value: 1440,
  },
];

// Includes the "non-delay" option for backwards compatability
const notificationOptions: Option[] = [nonDelayOption, ...emailNotificationDelayOptions];

const currencyOptions: Option[] = [];

Object.keys(currencies).map((currencyKey) => {
  const currency: Currency = currencies[currencyKey as keyof typeof currencies];
  currencyOptions.push({ value: currency.iso, label: currency.iso });
});

const commonCurrencyOptions = {
  USD: { value: currencies.USD.iso, label: currencies.USD.iso } as Option,
  CAD: { value: currencies.CAD.iso, label: currencies.CAD.iso } as Option,
};

const cmOption: Option = {
  label: DistanceUnit.CM,
  value: DistanceUnit.CM,
};

const inOption: Option = {
  label: DistanceUnit.IN,
  value: DistanceUnit.IN,
};

const mmOption: Option = {
  label: DistanceUnit.MM,
  value: DistanceUnit.MM,
};

const distanceOptions = [cmOption, inOption, mmOption];

const commonDistanceOptions = {
  cm: cmOption,
  in: inOption,
  mm: mmOption,
};

const timeRangeOptions7am9pm: Option[] = [];
for (let i = 7; i <= 21; i += 1) {
  const ampm = i < 12 ? 'am' : 'pm';
  const j = i < 13 ? i : i - 12;
  timeRangeOptions7am9pm.push({ label: `${j}:00${ampm}`, value: `${i}:00` } as Option);
  if (i < 21) {
    timeRangeOptions7am9pm.push({ label: `${j}:30${ampm}`, value: `${i}:30` } as Option);
  }
}

const countryOptions: Option[] = Object.values(countries).map((country) => {
  return {
    value: country.iso2,
    label: country.name,
  };
});

const carrierOptions: Option[] = Object.values(carriers).map((carrier) => {
  return {
    value: carrier.name,
    label: carrier.name,
  };
});

const carrierInOrderOptions: Option[] = Object.values(carriersInOrder).map((carrier) => {
  return {
    value: carrier.name,
    label: carrier.name,
  };
});

const storeNameToOption = (storeName: string) => ({
  value: storeName,
  label: storeName,
});
const storeOptions: Option[] = storeNames.map(storeNameToOption);

const storeOptionsInAscendingOrder: Option[] = orderBy(
  storeNames,
  [(storeName: string) => storeName.toLowerCase()],
  ['asc'],
).map(storeNameToOption);

const monthlyPackageOptions: Option[] = [
  {
    label: '0 - 10',
    value: '0 - 10',
  },
  {
    label: '11 - 100',
    value: '11 - 100',
  },
  {
    label: '101 - 500',
    value: '101 - 500',
  },
  {
    label: '501 - 1,000',
    value: '501 - 1,000',
  },
  {
    label: '1,001 - 5,000',
    value: '1,001 - 5,000',
  },
  {
    label: '5,001+',
    value: '5,001+',
  },
];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getValueFromOption = (option: Maybe<Option>, defaultValue: any = ''): any => {
  if (option) {
    return option.value;
  }

  return defaultValue;
};

const registrationSourceOptions: Option[] = [
  {
    label: 'Online Banner Ad',
    value: 'BANNER_AD',
  },
  {
    label: 'Radio',
    value: 'RADIO',
  },
  {
    label: 'Podcast',
    value: 'PODCAST',
  },
  {
    label: 'Social Media (Facebook, Twitter)',
    value: 'SOCIAL_MEDIA',
  },
  {
    label: 'Search (Google, Bing)',
    value: 'SEARCH',
  },
  {
    label: 'Referral',
    value: 'REFERRAL',
  },
];

export const CLOSE_ACCOUNT_MISSING_FEATURE: Option<string> = {
  value: 'missing_feature',
  label: 'account.plan.closeAccount.form.reasons.missingFeature',
};

export const CLOSE_ACCOUNT_HIGH_COST: Option<string> = {
  value: 'high_cost',
  label: 'account.plan.closeAccount.form.reasons.highCost',
};

export const CLOSE_ACCOUNT_BUSINESS_SHUTTING_DOWN: Option<string> = {
  value: 'business_shutting_down',
  label: 'account.plan.closeAccount.form.reasons.businessShuttingDown',
};

export const CLOSE_ACCOUNT_PREFERRED_COMPETITOR: Option<string> = {
  value: 'preferred_competitor',
  label: 'account.plan.closeAccount.form.reasons.preferredCompetitor',
};

export const CLOSE_ACCOUNT_OTHER: Option<string> = {
  value: 'other',
  label: 'account.plan.closeAccount.form.reasons.other',
};

const closeAccountReasonOptions = [
  CLOSE_ACCOUNT_MISSING_FEATURE,
  CLOSE_ACCOUNT_HIGH_COST,
  CLOSE_ACCOUNT_BUSINESS_SHUTTING_DOWN,
  CLOSE_ACCOUNT_PREFERRED_COMPETITOR,
  CLOSE_ACCOUNT_OTHER,
] as const;

const booleanOperatorOptions: Option[] = [equalOption];

export const TrueOption: Option<string> = {
  label: 'True',
  value: 'true',
};

export const FalseOption: Option<string> = {
  label: 'False',
  value: 'false',
};

const booleanFieldOptions: Option<string>[] = [TrueOption, FalseOption];

export const ShippingDestinationDomesticOption: Option<string> = {
  label: 'Domestic',
  value: 'domestic',
};

export const ShippingDestinationInternationalOption: Option<string> = {
  label: 'International',
  value: 'international',
};

export const ShipToCanadaOptions: Option<string>[] = [
  {
    label: 'Anywhere in Canada',
    value: ShippingCountryType.CA_country,
  },
  {
    label: 'Canada: postal/zip code',
    value: ShippingCountryType.CA_zipcode,
  },
  {
    label: 'Canada: province/territory',
    value: ShippingCountryType.CA_state,
  },
];

export const ShipToUnitedStatesOptions: Option<string>[] = [
  {
    label: 'Anywhere in the United States',
    value: ShippingCountryType.US_country,
  },
  {
    label: 'United States: zip code',
    value: ShippingCountryType.US_zipcode,
  },
  {
    label: 'United States: state',
    value: ShippingCountryType.US_state,
  },
];

export const ShippingDestinationGlobalOptions: Option<string>[] = [
  {
    label: 'All countries',
    value: ShippingCountryType.ALL_country,
  },
];

export const ShippingDestinationValueToOption = {
  [String(ShippingDestinationDomesticOption.value)]: ShippingDestinationDomesticOption,
  [String(ShippingDestinationInternationalOption.value)]: ShippingDestinationInternationalOption,
  [String(ShipToUnitedStatesOptions[0].value)]: ShipToUnitedStatesOptions[0],
  [String(ShipToUnitedStatesOptions[1].value)]: ShipToUnitedStatesOptions[1],
  [String(ShipToUnitedStatesOptions[2].value)]: ShipToUnitedStatesOptions[2],
  [String(ShipToCanadaOptions[0].value)]: ShipToCanadaOptions[0],
  [String(ShipToCanadaOptions[1].value)]: ShipToCanadaOptions[1],
  [String(ShipToCanadaOptions[2].value)]: ShipToCanadaOptions[2],
  [String(ShippingDestinationGlobalOptions[0].value)]: ShippingDestinationGlobalOptions[0],
};

const shippingDestinationOptions = [
  {
    label: '',
    options: [ShippingDestinationDomesticOption, ShippingDestinationInternationalOption],
  },
  {
    label: 'United States',
    options: ShipToUnitedStatesOptions,
  },
  {
    label: 'Canada',
    options: ShipToCanadaOptions,
  },
  {
    label: '',
    options: ShippingDestinationGlobalOptions,
  },
];

export default {
  weightOptions,
  commonWeightOptions,
  currencyOptions,
  commonCurrencyOptions,
  distanceOptions,
  commonDistanceOptions,
  timeRangeOptions7am9pm,
  countryOptions,
  nonDelayOption,
  emailNotificationDelayOptions,
  notificationOptions,
  carrierOptions,
  getValueFromOption,
  carrierInOrderOptions,
  monthlyPackageOptions,
  storeOptions,
  storeOptionsInAscendingOrder,
  relationalOperatorOptions,
  stringOperatorOptions,
  customsDeclaration,
  registrationSourceOptions,
  closeAccountReasonOptions,
  booleanFieldOptions,
  booleanOperatorOptions,
  shippingDestinationOptions,
};
